// src/api/cloche.api.ts — SCE Issues
// La cloche de l'appli (03/11) : lire, effacer et écrire les lignes de
// SCE_Notifications. Règles : utils/cloche.
//
// La liste est adressée par son NOM, comme SCE_Envois : aucune variable
// d'environnement. Lecture filtrée sur Destinataire (colonne INDEXÉE) : on ne
// lit que ses propres lignes, quelle que soit la taille de la liste.
//
// Absente (404) : `listerMesNotifications` rend null, la cloche ne s'affiche
// pas. Écriture : toujours au mieux, jamais bloquante — la notification n'est
// qu'un reflet de ce qui est déjà écrit ailleurs.

import { GraphApiError, graphDelete, graphGet, graphPost } from './graphClient'
import {
  champsNotificationAppli,
  lireNotification,
  NOM_LISTE_NOTIFICATIONS,
  trierNotifications,
  type NotificationAppli,
  type NouvelleNotification,
} from '@/utils/cloche'

const SITE_ID = import.meta.env.VITE_SHAREPOINT_SITE_ID ?? ''
const CHEMIN = `/sites/${SITE_ID}/lists/${NOM_LISTE_NOTIFICATIONS}/items`
const COLONNES = 'Title,Genre,Auteur,AuteurUPN,Quoi,Libelle,Extrait,Raison,Lien'

interface Page {
  value: { id: string; createdDateTime: string; fields: Record<string, unknown> }[]
  '@odata.nextLink'?: string
}

const echapper = (v: string): string => v.replace(/'/g, "''")

/** Mes notifications, les plus récentes d'abord ; null si la liste n'existe pas. */
export async function listerMesNotifications(upn: string): Promise<NotificationAppli[] | null> {
  const moi = upn.trim().toLowerCase()
  if (!moi) return []
  const filtre = encodeURIComponent(`fields/Destinataire eq '${echapper(moi)}'`)
  let suivante: string | null =
    `${CHEMIN}?$expand=fields($select=${COLONNES})&$select=id,createdDateTime&$filter=${filtre}&$top=200`
  const lues: NotificationAppli[] = []
  try {
    // Deux pages au plus : au-delà, ce n'est plus une cloche qu'on lit.
    for (let pages = 0; suivante && pages < 2; pages++) {
      const page: Page = await graphGet<Page>(suivante)
      for (const l of page.value) {
        const n = lireNotification(l.id, l.createdDateTime, l.fields)
        if (n) lues.push(n)
      }
      suivante = page['@odata.nextLink'] ?? null
    }
  } catch (err) {
    if (err instanceof GraphApiError && err.status === 404) return null
    throw err
  }
  return trierNotifications(lues)
}

/** Efface ces lignes ; une ligne déjà partie (autre onglet) n'est pas une erreur. */
export async function effacerNotifications(ids: readonly string[]): Promise<void> {
  await Promise.all(
    ids.map(async (id) => {
      try {
        await graphDelete(`${CHEMIN}/${encodeURIComponent(id)}`)
      } catch (err) {
        if (err instanceof GraphApiError && err.status === 404) return
        throw err
      }
    }),
  )
}

/** Écrit ces notifications, au mieux : un échec est noté dans la console, rien de plus. */
export async function deposerNotificationsAppli(liste: readonly NouvelleNotification[]): Promise<void> {
  const vues = new Set<string>()
  for (const n of liste) {
    const dest = n.destinataire.trim().toLowerCase()
    // Une ligne par personne et par notification. L'auteur est écarté par l'appelant
    // (sauf en essai des FNC-T, où tout lui revient).
    const cle = `${dest}|${n.reference}|${n.quoi}`
    if (!dest.includes('@') || vues.has(cle)) continue
    vues.add(cle)
    try {
      await graphPost<unknown>(CHEMIN, { fields: champsNotificationAppli(n) })
    } catch (err) {
      console.warn('[cloche] notification non écrite :', err)
      // Liste absente : inutile d'insister pour les suivantes.
      if (err instanceof GraphApiError && err.status === 404) return
    }
  }
}
